import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { Link } from "react-router-dom";

const DataPagination = ({ totalResults, resultsPerPage, currentPage }) => {
  const totalPages = Math.ceil(totalResults / resultsPerPage);
  const startResult = (currentPage - 1) * resultsPerPage + 1;
  const endResult = Math.min(currentPage * resultsPerPage, totalResults);

  // Generate page numbers for the sliding window (max 5 pages)
  const maxPageLinks = 5;
  const halfWindow = Math.floor(maxPageLinks / 2);
  let startPage = Math.max(1, currentPage - halfWindow);
  let endPage = Math.min(totalPages, startPage + maxPageLinks - 1);

  // Adjust startPage if endPage is at the maximum
  if (endPage - startPage + 1 < maxPageLinks) {
    startPage = Math.max(1, endPage - maxPageLinks + 1);
  }

  const pageNumbers = [];
  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  // Determine if ellipses are needed
  const showLeftEllipsis = startPage > 1;
  const showRightEllipsis = endPage < totalPages;

  return (
    <div className="flex items-center justify-between px-4 py-3 sm:px-6 min-w-full">
      {/* Mobile view: Previous/Next only*/}
      <div className="flex flex-1 justify-between sm:hidden">
        {currentPage > 1 ? (
          <Link
            to={`?page=${currentPage - 1}&limit=${resultsPerPage}`}
            className="relative inline-flex items-center rounded-md border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-cream-100"
          >
            Previous
          </Link>
        ) : (
          <span className="relative inline-flex items-center rounded-md border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 cursor-not-allowed opacity-50">
            Previous
          </span>
        )}
        {currentPage < totalPages ? (
          <Link
            to={`?page=${currentPage + 1}&limit=${resultsPerPage}`}
            className="relative ml-3 inline-flex items-center rounded-md border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-cream-100"
          >
            Next
          </Link>
        ) : (
          <span className="relative ml-3 inline-flex items-center rounded-md border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 cursor-not-allowed opacity-50">
            Next
          </span>
        )}
      </div>

      {/* Desktop view: Full pagination */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-ink-700">
            Showing <span className="font-medium">{startResult}</span> to{" "}
            <span className="font-medium">{endResult}</span> of{" "}
            <span className="font-medium">{totalResults}</span> results
          </p>
        </div>
        <div>
          <nav
            aria-label="Pagination"
            className="isolate inline-flex -space-x-px rounded-md shadow-sm"
          >
            {/* Previous Button */}
            {currentPage > 1 ? (
              <Link
                to={`?page=${currentPage - 1}&limit=${resultsPerPage}`}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-ink-400 ring-1 ring-inset ring-ink-200 hover:bg-cream-100 focus:z-20 focus:outline-offset-0"
              >
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon aria-hidden="true" className="size-5" />
              </Link>
            ) : (
              <span className="relative inline-flex items-center rounded-l-md px-2 py-2 text-ink-400 ring-1 ring-inset ring-ink-200 cursor-not-allowed opacity-50">
                <span className="sr-only">Previous</span>
                <ChevronLeftIcon aria-hidden="true" className="size-5" />
              </span>
            )}

            {/* Page Numbers */}
            {showLeftEllipsis && (
              <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-ink-700 ring-1 ring-inset ring-ink-200 focus:outline-offset-0">
                ...
              </span>
            )}
            {pageNumbers.map((page) => (
              <Link
                key={page}
                to={`?page=${page}&limit=${resultsPerPage}`}
                aria-current={page === currentPage ? "page" : undefined}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                  page === currentPage
                    ? "z-10 bg-accent-500 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-500"
                    : "text-ink-900 ring-1 ring-inset ring-ink-200 hover:bg-cream-100 focus:z-20 focus:outline-offset-0"
                }`}
              >
                {page}
              </Link>
            ))}
            {showRightEllipsis && (
              <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-ink-700 ring-1 ring-inset ring-ink-200 focus:outline-offset-0">
                ...
              </span>
            )}

            {/* Next Button */}
            {currentPage < totalPages ? (
              <Link
                to={`?page=${currentPage + 1}&limit=${resultsPerPage}`}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-ink-400 ring-1 ring-inset ring-ink-200 hover:bg-cream-100 focus:z-20 focus:outline-offset-0"
              >
                <span className="sr-only">Next</span>
                <ChevronRightIcon aria-hidden="true" className="size-5" />
              </Link>
            ) : (
              <span className="relative inline-flex items-center rounded-r-md px-2 py-2 text-ink-400 ring-1 ring-inset ring-ink-200 cursor-not-allowed opacity-50">
                <span className="sr-only">Next</span>
                <ChevronRightIcon aria-hidden="true" className="size-5" />
              </span>
            )}
          </nav>
        </div>
      </div>
    </div>
  );
};

export default DataPagination;
